// Reminders for the View Hours page: what a submitted vacation plan counts on
// in a pay period - extra hours to work before the trip, time off taken
// unpaid - so the timesheet being looked at says so.
//
// Only submitted plans with at least one request still live (pending or
// approved) remind: a plan never submitted is only an idea, and one whose
// requests have all been withdrawn is not happening.
//
// The figures per pay period are the ones the plan saved (summary
// extraByPeriod / unpaidByPeriod). A plan saved before those existed falls
// back to its answers: extra hours per period as entered, and unpaid hours
// against the one pay period the trip sits in - or, for a trip across more
// than one, against the trip as a whole.

import { payPeriodFor } from './vacationPlan';

const round2 = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;

const isPlainObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

const isLive = (request) => {
  const status = String(request?.status || '').toLowerCase();
  return status === 'pending' || status === 'approved';
};

const overlaps = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd;

/**
 * @param {object} args
 * @param {Array}  args.plans     saved plans (summary, inputs, submission)
 * @param {Array}  args.requests  the employee's time-off requests
 * @param {string} args.start     the range being viewed, YYYY-MM-DD
 * @param {string} args.end
 * @returns {Array} { planId, planName, planStart, planEnd, periodId, periodLabel, extraHours, unpaidHours }
 */
export const planRemindersForRange = ({ plans = [], requests = [], start, end }) => {
  if (!start || !end) return [];
  const requestsById = new Map(requests.map((request) => [String(request.id), request]));
  const reminders = [];

  plans.forEach((plan) => {
    const ids = Array.isArray(plan.submission?.requestIds) ? plan.submission.requestIds : [];
    if (!ids.some((id) => isLive(requestsById.get(String(id))))) return;

    const summary = plan.summary || {};
    const inputs = plan.inputs || {};
    const planStart = String(plan.start_date).slice(0, 10);
    const planEnd = String(plan.end_date).slice(0, 10);
    const base = { planId: plan.id, planName: plan.name, planStart, planEnd };

    const extra = isPlainObject(summary.extraByPeriod)
      ? summary.extraByPeriod
      : (inputs.wantsExtra && isPlainObject(inputs.extraByPeriod) ? inputs.extraByPeriod : {});

    let unpaid = isPlainObject(summary.unpaidByPeriod) ? summary.unpaidByPeriod : null;
    let unpaidAcrossTrip = 0;
    if (!unpaid) {
      unpaid = {};
      const total = round2(summary.unpaidHours ?? (inputs.wantsUnpaid ? inputs.unpaidHours : 0));
      if (total > 0) {
        const first = payPeriodFor(planStart).id;
        if (first === payPeriodFor(planEnd).id) unpaid[first] = total;
        else unpaidAcrossTrip = total;
      }
    }

    [...new Set([...Object.keys(extra), ...Object.keys(unpaid)])].forEach((periodId) => {
      const period = payPeriodFor(periodId);
      if (!period || !overlaps(period.startDate, period.endDate, start, end)) return;
      const extraHours = round2(extra[periodId]);
      const unpaidHours = round2(unpaid[periodId]);
      if (extraHours <= 0 && unpaidHours <= 0) return;
      reminders.push({ ...base, periodId, periodLabel: period.label, extraHours, unpaidHours });
    });

    if (unpaidAcrossTrip > 0 && overlaps(planStart, planEnd, start, end)) {
      reminders.push({ ...base, periodId: null, periodLabel: null, extraHours: 0, unpaidHours: unpaidAcrossTrip });
    }
  });

  return reminders.sort((a, b) => (
    String(a.periodId || a.planStart).localeCompare(String(b.periodId || b.planStart))
    || String(a.planStart).localeCompare(String(b.planStart))
  ));
};
